/* 
  Measurement Input
  - Handles the input of the measurement service form
  - Handles the unit change
  - Handles the error message
  - Validates the input
  */
class MeasurementInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("[data-mes-input]");
    this.unitIndicator = this.querySelector("[data-unit-indicator]");
    this.errorMessage = this.querySelector("[data-error-for]");
    this.unit = this.input?.dataset.unit || "in";
    
    document.addEventListener("unit-change", this.unitChangeHandler.bind(this));

    this.input?.addEventListener("blur", this.validate.bind(this));
    if (this.input && this.input.tagName.toLowerCase() === "select") {
      this.input.addEventListener("change", this.validate.bind(this));
    }
  }

  setUnit(unit) {
    this.unit = unit;
    if (this.unitIndicator) this.unitIndicator.textContent = this.unit;
    if (this.input) this.input.dataset.unit = this.unit;
  }

  // Unit change handler
  unitChangeHandler(e) {
    if (!e?.detail?.unit || e.detail.unit === this.unit) return;

    const oldUnit = this.unit;
    this.unit = e.detail.unit;

    // Convert input value
    if (
      this.input?.value &&
      this.input.type !== "checkbox" &&
      this.input.type !== "radio" &&
      this.input.tagName.toLowerCase() !== "select"
    ) {
      const newValue = measurementHelpers.convertValue(
        this.input.value,
        oldUnit,
        this.unit
      );
      this.input.value = newValue;
    }

    this.setUnit(this.unit);
  }

  // Validate the input against its constraints
  validate({ silent = false }) {
    if (!silent) this.clearError();
    let isValid = true;
    let errorMessage = "";
    if (!this.input) {
      isValid = false;
      errorMessage = "Missing input";
    }

    // Checkbox validation
    if (this.input.type === "checkbox") {
      const name = this.input.name;
      const checkBoxes = this.querySelectorAll(`input[name="${name}"]`);
      const checked = Array.from(checkBoxes).some(
        (checkbox) => checkbox.checked
      );
      if (!checked) {
        errorMessage = "This field is required";
        isValid = false;
      }
    }

    // Required validation
    if (this.input.required && !this.input.value) {
      errorMessage = "This field is required";
      isValid = false;
    }

    // Validate numeric inputs
    if (
      this.input.type !== "checkbox" &&
      this.input.type !== "radio" &&
      this.input.tagName.toLowerCase() !== "select"
    ) {
      const value = parseFloat(this.input.value);

      // Check if it's a valid number
      if (isNaN(value)) {
        errorMessage = "Please enter a valid number";
        isValid = false;
      }

      // Get min/max based on current unit
      const unit = this.unit;
      let min, max;

      if (unit === "in") {
        min =
          this.input.dataset.mesMinIn !== ""
            ? parseFloat(this.input.dataset.mesMinIn)
            : null;
        max =
          this.input.dataset.mesMaxIn !== ""
            ? parseFloat(this.input.dataset.mesMaxIn)
            : null;
      } else {
        min =
          this.input.dataset.mesMinCm !== ""
            ? parseFloat(this.input.dataset.mesMinCm)
            : null;
        max =
          this.input.dataset.mesMaxCm !== ""
            ? parseFloat(this.input.dataset.mesMaxCm)
            : null;
      }

      // Check min constraint
      if (min !== null && !isNaN(min) && value < min) {
        errorMessage = `Value must be at least ${min} ${unit}`;
        isValid = false;
      }

      // Check max constraint
      if (max !== null && !isNaN(max) && value > max) {
        errorMessage = `Value cannot exceed ${max} ${unit}`;
        isValid = false;
      }
    }

    if (errorMessage && !silent) {
      this.showError(errorMessage);
    }
    return isValid;
  }

  showError(message) {
    this.input?.classList.add("error");
    this.querySelectorAll('.mes-input').forEach(input => {
      input.classList.add('error');
    });
    if (this.errorMessage) {
      this.errorMessage.textContent = message;
    }
    return false;
  }

  clearError() {
    this.input?.classList.remove("error");
    this.querySelectorAll('.mes-input').forEach(input => {
      input.classList.remove('error');
    });
    if (this.errorMessage) {
      this.errorMessage.textContent = "";
    }
  }
}

if (!customElements.get("measurement-input")) {
  customElements.define("measurement-input", MeasurementInput);
}



/* 
  Height Input Component
  - Extends MeasurementInput to handle height inputs specifically
  - Splits height into feet and inches when in imperial units
  - Handles conversion between cm and ft/in
  - Manages visibility of proxy inputs vs main input based on unit
*/
class HeightInput extends MeasurementInput {
  constructor() {
    super();
    this.mainInputContainer = this.querySelector("[data-main-input]");
    this.mainInput = this.querySelector("[data-main-input] input");
    this.proxyInputs = Array.from(this.querySelectorAll("[data-proxy-input]"));
    this.ftInput = this.querySelector('[data-proxy-input][data-unit="ft"]');
    this.inInput = this.querySelector('[data-proxy-input][data-unit="in"]');
    this.multiInputContainer = this.querySelector("[data-proxy-container]");
    
    // Initialize visibility based on current unit
    this.updateVisibility();
    
    // Add event listeners for proxy inputs
    this.proxyInputs.forEach(input => {
      input.addEventListener('input', this.handleProxyInput.bind(this));
      input.addEventListener('blur', this.validate.bind(this));
    });

  }
  
  // Override the parent's unitChangeHandler method
  unitChangeHandler(e) {
    // Call the parent method first to handle basic unit conversion
    super.unitChangeHandler(e);
    
    // Then handle the additional behavior specific to HeightInput
    if (e?.detail?.unit === "in") {
      this.updateProxyInputs();
    }
    this.updateVisibility();
  }
  
  // Convert ft/in to total inches and update main input
  handleProxyInput() {
    if (this.unit !== "in") return;
    
    const feet = parseFloat(this.ftInput.value) || 0;
    const inches = parseFloat(this.inInput.value) || 0;
    
    // Calculate total inches
    const totalInches = (feet * 12) + inches;
    
    // Update main input
    if (this.mainInput) {
      this.mainInput.value = totalInches;
      // Trigger change event on main input
      this.mainInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  
  // Update the ft/in proxy inputs based on the main input value
  updateProxyInputs() {
    if (!this.mainInput?.value) {
      if (this.ftInput) this.ftInput.value = '';
      if (this.inInput) this.inInput.value = '';
      return;
    }
    
    // Get total inches and convert to feet and inches
    const totalInches = parseFloat(this.mainInput.value);
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round((totalInches % 12) * 100) / 100;
    
    // Update proxy inputs
    if (this.ftInput) this.ftInput.value = feet;
    if (this.inInput) this.inInput.value = inches;
  }
  
  // Toggle visibility of inputs based on current unit
  updateVisibility() {
    
    if (this.unit === "in") {
      // Show proxy inputs, hide main input
      if (this.mainInputContainer) this.mainInputContainer.classList.add('visually-hidden');
      if (this.multiInputContainer) this.multiInputContainer.classList.remove('visually-hidden');
    } else {
      // Show main input, hide proxy inputs
      if (this.mainInputContainer) this.mainInputContainer.classList.remove('visually-hidden');
      if (this.multiInputContainer) this.multiInputContainer.classList.add('visually-hidden');
    }
  }
}

if (!customElements.get("height-input")) {
  customElements.define("height-input", HeightInput);
} 